import {Injectable} from "@angular/core";
import {HttpService} from "./http.service";
import {HttpParams} from "@angular/common/http";
import {AddIntervenorResponse, DeleteSecondaryUserResponse, InviteSecondaryUserResponse, PartiesResponse} from "../models/parties";
import {AttorneyInfoResponse} from "../models/parties";
import {IntervenorCompanyInfo} from "../models/user";

@Injectable({
  providedIn: 'root'
})
export class PartiesService {

  constructor(private httpService: HttpService) {
  }

  getListOfParties(aNum: string) {
    const options = { headers: this.httpService.jsonHeaders };
    const url = '/view-manage-parties?aNum=' + aNum;

    return this.httpService.Post<PartiesResponse>(url,  new HttpParams(), options);
  }

  getPrimaryRepsOrAgencyRepsInfo(repEmail: string, inviter_Type: string, typeOfRequest: string, companyName: string, companyAddr: string, aNum: string) {
    const formData: any = {
      email : repEmail,
      aNum : aNum,
      companyName : companyName,
    }

    if (typeOfRequest === 'Assign Agency Representative' && inviter_Type !== 'SA') {
      formData.inviter_Type = 'agency-attorney';
    } else if (inviter_Type === 'P') {
      formData.inviter_Type = 'protester';
    } else if (inviter_Type === 'I') {
      formData.inviter_Type = 'intervenor';
    } else if (inviter_Type === 'SA') {
      formData.inviter_Type = 'secondary-agency';
    }

    const httpParams =  this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/get-attorney-info'

    return this.httpService.Post<AttorneyInfoResponse>(url, httpParams, options);
  }

  validateInvitationRequests(data: any, typeOfRequest: string, inviter_Type: string, companyName: string, companyAddr: string, aNum: string) {
    switch (data.validation) {
      case 'noSuchEmail': {
        return 'There is no EPDS user account associated with that email. Please enter a different email address or check with the user you are attempting to add to see if the user has an account.'
      }
      case 'notFromSameAgency': {
        return 'The user you are trying to add is associated with another agency. You can only add representatives from the same agency. ';
      }
      case 'invalidAgency': {
        return 'This is not an EPDS registered agency account. ';
      }
      case 'notASecondaryAgency': {
        return 'You cannot add primary agency representative as a secondary agency representative. ';
      }
      case 'alreadyInvited': {
        return 'The user you are trying to add has a pending invitation to join the case.';
      }
      case 'hasAccess': {
        return 'The user you are trying to add has either already joined the case or has a pending invitation to join the case.';
      }
      case 'sameEmail': {
        return 'Same email';
      }
      case 'valid': {
        return null;
      }
      default: {
        return 'Unexpected error in validation';
      }
    }
  }

  confirmPrimaryOrAgencyRepInfo(repEmail: string, inviter_Type: string, typeOfRequest: string, companyName: string, companyAddr: string, aNum: string) {
    const formData: any = {
      email : repEmail,
      aNum : aNum,
    }

    if (typeOfRequest === 'Assign Agency Representative') {
      formData.assignType = 'agency-attorney'
    } else if (inviter_Type === 'P') {
      formData.assignType = 'primary-protester'
    } else if (inviter_Type === 'I') {
      formData.assignType = 'primary-intervenor'
      formData.companyName = companyName
      formData.companyAddr = companyAddr
    } else if (inviter_Type === 'SA') {
      formData.assignType = 'secondary-agency'
    }

    const httpParams =  this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/assign-rep'

    return this.httpService.Post<any>(url, httpParams, options);
  }

  deleteSecondaryUser(secondaryRepUserId: string, aNum: string) {
    const formData: any = {
      secondary_User_Id : secondaryRepUserId
    }

    if (aNum) {
      formData.aNum = aNum;
    }

    const httpParams =  this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/delete-secondary-user'

    return this.httpService.Post<DeleteSecondaryUserResponse>(url, httpParams, options);
  }

  invitePartiesToTheCase(repEmail: string, inviter_Type: string, typeOfRequest: string, companyName: string, companyAddr: string, aNum: string) {
    const formData: any = {
      email: repEmail,
      aNum: aNum
    }

    if (inviter_Type === 'P') {
      formData.inviter_Type = 'protester';
    } else if (inviter_Type === 'I') {
      formData.inviter_Type = 'intervenor';
      formData.companyName = companyName;
      formData.companyAddr = companyAddr;
    } else if (inviter_Type === 'SA') {
      formData.inviter_Type = 'secondary-agency';
    }

    const httpParams =  this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/invite-secondary-user'

    return this.httpService.Post<InviteSecondaryUserResponse>(url, httpParams, options);
  }

  updateProtectiveOrder(userId: string, updateStatus: string, aNum: string) {
    const formData = {
      shouldAdmit: updateStatus,
      userId: userId,
      aNum: aNum
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    const url = '/admit-to-po'

    return this.httpService.Post<any>(url, httpParams, options);
  }

  updatePartyInfo(formData: any, partyType: string) {
    formData.aNum = formData.a_No;
    formData.companyName = formData.company_Name;
    formData.address1 = formData.company_address1;
    formData.address2 = formData.company_address2;
    formData.city = formData.company_City;
    formData.state = formData.company_State;
    formData.zipCode = formData.company_Zipcode;
    formData.country = formData.company_Country;
    formData.partyType = partyType;

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    const url = '/update-party-info'

    return this.httpService.Post<any>(url, httpParams, options);
  }

  uploadEPDSDocs(a_No: string, fileIdentifierCode: string, attachmentType: string, fileName: string, file: File, fileData : ArrayBuffer) {
    const formData = {
      aNum: a_No, fileIdentifierCode, attachmentType,
      flowChunkNumber: 1,
      flowTotalChunks: 1,
      flowChunkSize: 1000000,
      flowCurrentChunkSize: fileData.byteLength,
      flowTotalSize: fileData.byteLength,
      flowIdentifier: '6pci',
      flowFilename: fileName,
      flowRelativePath: fileName,
      file: fileData
    };

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    const url = '/upload-epds-documents'

    return this.httpService.Post<any>(url, httpParams, options);
  }

  addIntervenor(protestId: string, address1: string, address2: string, city: string, state: string, zipCode: string, country: string, comments: string | undefined, intervenorCompanyName: string, intervenorEmailAddress: string) {
    const formData = {
      protestId, address1, address2, city, state, zipCode, country, comments, intervenorCompanyName, intervenorEmailAddress
    };

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    const url = '/add-attachments/intervene'

    return this.httpService.Post<AddIntervenorResponse>(url, httpParams, options);
  }

  // removeIntervenor(aNum: string, intervenorFileId: number | undefined, companyName: string, companyAddress: string | undefined, companyDetail: string | undefined) {
  removeIntervenor(aNum: string, companyInfo: IntervenorCompanyInfo) {
    const formData = {
      ...companyInfo,
      aNum
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    const url = '/remove-intervenor'

    return this.httpService.Post<any>(url, httpParams, options);
  }

}
